import { zodResolver } from '@hookform/resolvers/zod'
import { router } from 'expo-router'
import { useForm } from 'react-hook-form'
import { Portal } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'
import z from 'zod'

import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldText from '@/components/form/FormFieldText'
import UniButton from '@/components/unistyles/UniButton'
import UniModal from '@/components/unistyles/UniModal'
import FosterService from '@/services/fosterService'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'

interface Props {
  open: boolean
  onClose: () => void
  fosterId: string
}

const newAdoptionRecordSchema = z.object({
  returnDate: z.date(),
  returnReason: z.string(),
})

type NewAdoptionRecordForm = z.infer<typeof newAdoptionRecordSchema>

const ReturnAdoptionAction = ({ open, onClose, fosterId }: Props) => {
  const { foster, refreshFoster } = FosterService.useFoster(fosterId)

  const adoptionReturnMutation = trpc.foster.adoptions.return.useMutation()

  const { control, formState, handleSubmit, reset } =
    useForm<NewAdoptionRecordForm>({
      mode: 'onBlur',
      resolver: zodResolver(newAdoptionRecordSchema),
      defaultValues: {
        returnDate: new Date(),
        returnReason: '',
      },
    })

  if (!foster) {
    return null
  }
  const onSubmit = handleSubmit(async (data) => {
    await adoptionReturnMutation.mutateAsync({
      data: {
        ...data,
      },
      fosterId: fosterId,
      organizationId: foster?.organizationId,
    })

    refreshFoster()

    router.push(buildOrgPath(`/fosters/${fosterId}/adoptions`))

    reset()
    onClose()
  })

  return (
    <Portal>
      <UniModal
        contentContainerStyle={styles.root}
        onDismiss={onClose}
        visible={open}
      >
        <FormFieldDate
          control={control}
          errors={formState.errors}
          fieldName="returnDate"
          label="Return Date"
          required
          submitBehavior="blurAndSubmit"
        />
        <FormFieldText
          control={control}
          errors={formState.errors}
          fieldName="returnReason"
          label="Reason for Return"
          required
          submitBehavior="blurAndSubmit"
        />
        <UniButton
          disabled={adoptionReturnMutation.isPending}
          loading={adoptionReturnMutation.isPending}
          mode="contained"
          onPress={onSubmit}
          style={styles.button}
          testID="return-adoption-button"
        >
          Mark Returned
        </UniButton>
      </UniModal>
    </Portal>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    button: {
      marginTop: theme.tokens.spacing[3],
    },
    root: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.tokens.containerBorderRadius,
      gap: 8,
      justifyContent: 'center',
      margin: 8,
      padding: theme.tokens.spacing[6],
    },
  }
})

export default ReturnAdoptionAction
